import React, { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { throttle } from 'lodash-es';

import Portal from '../components/Portal';
import { useEditorNode } from '../hooks/useEditorNode';
import { useMutationObserver } from '../hooks/useMutationObserver';
import { useResizeObserver } from '../hooks/useResizeObserver';
import { COLORS } from '../styles';
import {
  DocumentComponent,
  DocumentComponentOptions,
  DocumentPath,
} from '../types';

export interface DecoratedProps {
  path: DocumentPath;
}

interface Offset {
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
}

export function decorateComponent<T extends {}>(
  component: React.ElementType<T>,
  display: 'block' | 'inline-block' | 'inline' = 'block',
  options: DocumentComponentOptions<T> = {},
): DocumentComponent {
  const Result = (props: T & DecoratedProps): React.ReactElement => {
    const { path } = props;
    const { isSelected, isHovered, displayMode, onSelect, onHover, onUnhover } =
      useEditorNode(path);

    function handleMouseOver(e: MouseEvent) {
      e.stopPropagation();
      if (isHovered) { return; }
      onHover(path);
    }

    function handleMouseOut() {
      onUnhover();
    }

    function handleClick(e: MouseEvent) {
      e.stopPropagation();
      onSelect();
    }

    const container = useRef<HTMLSpanElement>(null);
    const [offset, setOffset] = useState<Offset>({});
    const updateOffset = throttle(() => {
      const renderer = container.current?.querySelector('[data-id=\'renderer\']');
      if (!renderer) { return; }

      let combinedOffset: Offset = {};
      function combineOffset(element: Element) {
        if (
          getComputedStyle(element).overflow === 'visible' &&
          element.children.length > 0
        ) {
          Array.from(element.children).map(combineOffset);
        }

        const { top, left, right, bottom } = element.getBoundingClientRect();

        combinedOffset = {
          top: combinedOffset.top ? Math.min(combinedOffset.top, top) : top,
          left: combinedOffset.left
            ? Math.min(combinedOffset.left, left)
            : left,
          right: combinedOffset.right
            ? Math.max(combinedOffset.right, right)
            : right,
          bottom: combinedOffset.bottom
            ? Math.max(combinedOffset.bottom, bottom)
            : bottom,
        };
      }

      Array.from(renderer.children).map(combineOffset);
      setOffset(combinedOffset);
    });

    useEffect(() => {
      function handleScroll() {
        if (!isHovered && !isSelected) { return; }
        updateOffset();
      }
      const canvasScroller = document.querySelector('#canvas-scroller');
      window.addEventListener('scroll', handleScroll, false);
      canvasScroller?.addEventListener('scroll', handleScroll, false);
      return () => {
        window.removeEventListener('scroll', handleScroll);
        canvasScroller?.removeEventListener('scroll', handleScroll);
      };
    }, [isHovered, isSelected]);

    useMutationObserver({
      target: container,
      config: {
        childList: true,
        subtree: true,
      },
      onChange: updateOffset,
    });

    useResizeObserver({
      target: () => document.querySelector('#canvas'),
      onChange: updateOffset,
    });

    const innerElement = useMemo(
      () => (
        <span data-id="renderer">{ React.createElement(component, props) }</span>
      ),
      [props],
    );

    if (displayMode === 'edit') {
      const canvasRect = document
        .querySelector('#canvas')
        ?.getBoundingClientRect() || {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      };
      const highlighterStyle = {
        top: `${Math.max(offset.top || 0, canvasRect.top)}px`,
        left: `${Math.max(offset.left || 0, canvasRect.left)}px`,
        width: `${
          Math.min(offset.right || 0, canvasRect.right) -
          Math.max(offset.left || 0, canvasRect.left)
        }px`,
        height: `${
          Math.min(offset.bottom || 0, canvasRect.bottom - 1) -
          Math.max(offset.top || 0, canvasRect.top)
        }px`,
      };

      return (
        <span
          ref={container}
          onClick={handleClick}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          style={{
            ...options.getWrapperStyle?.(props) || {},
            zIndex: isHovered ? 1 : 0,
          }}
        >
          { innerElement }
          { (isHovered || isSelected) && (
            <Portal containerSelector="#highlighter-root">
              <Highlighter
                label={options.label}
                style={highlighterStyle}
                color={isHovered ? COLORS.red50 : COLORS.blue50}
              />
            </Portal>
          ) }
        </span>
      );
    }

    return React.createElement(component, props);
  };

  Object.assign(Result, options);
  if (typeof component !== 'string') {
    Result.displayName = component.displayName;
  }

  return Result;
}

const Highlighter = styled.div<{
  color: typeof COLORS[keyof typeof COLORS],
  label?: string,
}>`
  position: fixed;
  min-width: 1px;
  min-height: 1px;
  box-shadow: 0 0 0 1px ${({ color }) => color};
  pointer-events: none;
  z-index: 2;
  &:after {
    content: ${({ label }) => (label ? `"${label}"` : 'none')};
    position: absolute;
    left: -1px;
    bottom: 100%;
    padding: 1px 4px;
    color: ${COLORS.gray0};
    font-size: 10px;
    line-height: 1.2;
    white-space: nowrap;
    background: ${({ color }) => color};
  }
`;
